<template>
  <div class="card-body">
    <!-- Vehicle Details -->
    <div class="form-row">
      <div class="col-12">
        <h4>{{ trans('Vehicle Details') }}</h4>
        <hr class="solid mt-0">
      </div>
    </div>
    <div class="form-row">
      <!-- Manufacturer -->
      <div class="form-group required col-lg-4">
        <label class="font-weight-bold text-dark text-2">{{ trans('Manufacturer') }}</label>
        <v-select
          v-model="manufacturer"
          taggable
          :class="{ error: hasError('vehicleManufacturer') }"
          :placeholder="trans('Manufacturer')"
          :loading="!manufacturers || !manufacturers.length"
          :disabled="!manufacturers || !manufacturers.length"
          :options="manufacturers"
          :select-on-tab="true"
          :create-option="newOption => {
            newOption = { label: newOption }

            this.$emit('option:created', newOption)
            return newOption
          }"
          @change="$emit('update:vehicleManufacturer', manufacturer.label)"
          @input="$emit('update:vehicleManufacturer', manufacturer.label)"
        />

        <label v-if="errors.vehicleManufacturer" class="error">{{ errors.vehicleManufacturer[0] }}</label>
        <label v-else-if="errors.vehicleModel" class="error">{{ errors.vehicleModel[0] }}</label>
        <label v-else-if="errors.vehicleBodyType" class="error">{{ errors.vehicleBodyType[0] }}</label>
        <label v-else class="error" v-if="hasError('vehicle')"></label>
      </div>

      <!-- Model -->
      <div class="form-group required col-lg-4">
        <label class="font-weight-bold text-dark text-2">{{ trans('Model') }}</label>
        <v-select
          v-model="model"
          taggable
          :class="{ error: hasError('vehicleModel') }"
          :placeholder="trans('Model')"
          :disabled="!manufacturer"
          :options="models"
          :select-on-tab="true"
          :create-option="newOption => {
            this.$emit('option:created', newOption)
            return newOption
          }"
          @change="$emit('update:vehicleModel', model)"
          @input="$emit('update:vehicleModel', model)"
        />
      </div>

      <!-- Body Type -->
      <div class="form-group col-lg-4">
        <label class="font-weight-bold text-dark text-2">{{ trans('Body Shape') }}</label>
        <v-select
          v-model="bodyType"
          :class="{ error: hasError('vehicleBodyType') }"
          :placeholder="trans('Body Shape')"
          :reduce="option => option.value"
          :options="allBodyTypes"
          :select-on-tab="true"
          @change="$emit('update:vehicleBodyType', bodyType)"
          @input="$emit('update:vehicleBodyType', bodyType)"
        />
      </div>

      <!-- Production Year -->
      <div class="form-group col-lg-4">
        <label class="font-weight-bold text-dark text-2">{{ trans('Production Year') }}</label>
        <input
          type="text"
          pattern="[1-2][0-9][0-9][0-9]"
          maxlength="4"
          :placeholder="trans('Production Year')"
          v-model="prodYear"
          class="form-control"
          :class="{ error: hasError('production_year') }"
          autocomplete="off"
          @change="$emit('update:productionYear', prodYear)"
          @input="$emit('update:productionYear', prodYear)"
        />
        <label class="error" v-if="hasError('production_year')">{{ errors.production_year[0] }}</label>
      </div>

      <!-- Color -->
      <div class="form-group col-lg-4">
        <label class="font-weight-bold text-dark text-2">{{ trans('Color') }}</label>
        <div class="row">
          <div class="col-8">
            <v-select
              v-model="color.color"
              @change="$emit('update:color', color)"
              :class="{ error: hasError('color.color') }"
              :placeholder="trans('Color')"
              :reduce="option => option.value"
              :loading="!colors || !colors.length"
              :disabled="!colors || !colors.length"
              :options="colors"
            >
              <template v-slot:option="option">
                <div class="color-preview" :style="'background-color: ' + option.color"></div>
                {{ option.label }}
              </template>

              <template v-slot:selected-option="option">
                <div class="color-preview" :style="'background-color: ' + option.color"></div>
                {{ option.label }}
              </template>
            </v-select>
          </div>
          <div class="col-4 px-0 d-flex align-items-center">
            <div class="custom-control custom-checkbox">
              <input
                type="checkbox"
                class="custom-control-input"
                id="is_color_metallic"
                @change="$emit('update:color', color)"
                v-model="color.is_metallic"
                :class="{ error: hasError('color') }"
              />
              <label class="custom-control-label" for="is_color_metallic">{{ trans('Metallic') }}</label>
            </div>
          </div>
        </div>

        <label class="error" v-if="hasError('color.color')">{{ errors['color.color'][0] }}</label>
      </div>

      <!-- VIN -->
      <div class="form-group col-lg-4">
        <label for="vin" class="font-weight-bold text-dark text-2">{{ trans('VIN') }}</label>
        <input
          id="vin"
          type="text"
          :placeholder="trans('VIN')"
          v-model="vinValue"
          maxlength="17"
          class="form-control"
          :class="{ error: hasError('vin') }"
          autocomplete="off"
          @change="$emit('update:vin', vinValue)"
          @input="$emit('update:vin', vinValue)"
        />
        <label class="error" v-if="hasError('vin')">{{ errors.vin[0] }}</label>
      </div>
    </div>

    <!-- Registration Details -->
    <div class="form-row mt-5">
      <div class="col-12">
        <h4>{{ trans('Registration Details') }}</h4>
        <hr class="solid mt-0">
      </div>
    </div>

    <div class="form-row align-items-baseline">
      <!-- First Registration -->
      <div class="form-group col-lg-4">
        <label class="font-weight-bold text-dark text-2">{{ trans('First Registration') }}</label>
        <div class="row">
          <div class="col-6 pr-0">
            <v-select
              v-model="regDate.month"
              @input="$emit('update:reg_date', regDate)"
              :class="{ error: hasError('reg_date.month') }"
              :placeholder="trans('Month')"
              :reduce="option => option.value"
              :disabled="!months || !months.length"
              :clearable="false"
              :options="months"
            />
          </div>
          <div class="col-6">
            <v-select
              v-model="regDate.year"
              @input="$emit('update:reg_date', regDate)"
              :class="{ error: hasError('reg_date.year') }"
              :placeholder="trans('Year')"
              :disabled="!regYears || !regYears.length"
              :clearable="false"
              :options="regYears"
            />
          </div>

          <label class="error" v-if="hasError('reg_date.month')">{{ errors['reg_date.month'][0] }}</label>
          <label class="error" v-if="hasError('reg_date.year') && !hasError('reg_date.month')">{{ errors['reg_date.year'][0] }}</label>
        </div>
      </div>

      <!-- Registration Plate -->
      <div class="form-group col-lg-4">
        <label for="reg_plate" class="font-weight-bold text-dark text-2">{{ trans('Registration Plate') }}</label>
        <input
          id="reg_plate" type="text"
          :placeholder="trans('Registration Plate')"
          v-model="regPlate"
          class="form-control" :class="{ error: hasError('reg_plate') }"
        />
        <label class="error" v-if="hasError('reg_plate')">{{ errors['reg_plate'][0] }}</label>
      </div>
    </div>

    <!-- Registration Details -->
    <div class="form-row mt-5">
      <div class="col-12">
        <h4>{{ trans('Motorization') }}</h4>
        <hr class="solid mt-0">
      </div>
    </div>
    <div class="form-row align-items-baseline">
      <!-- Fuel Type  -->
      <div class="form-group col-lg-4">
        <label class="font-weight-bold text-dark text-2">{{ trans('Fuel') }}</label>
        <v-select
          v-model="fuelType"
          :class="{ error: hasError('fuel') }"
          :placeholder="trans('Fuel')"
          :reduce="options => options.value"
          :loading="!fuelTypes || !fuelTypes.length"
          :disabled="!fuelTypes || !fuelTypes.length"
          :options="fuelTypes"
        />
        <label class="error" v-if="hasError('fuel')">{{ errors['fuel'][0] }}</label>
      </div>

      <!-- Power -->
      <power-input
        v-model="powerValue"
        :unit="powerUnit"
        @unitChange="value => this.powerUnit = value"
        @hpValueChange="powerHpHandler"
      />

      <!-- Cylinder Capacity -->
      <capacity-input
        v-model="capacityValue"
        :unit="capacityUnit"
        @unitChange="value => this.capacityUnit = value"
        @ccValueChange="capacityCcHandler"
      />
    </div>
    <div class="form-row align-items-baseline">
      <!-- Gearbox  -->
      <div class="form-group col-lg-4">
        <label class="font-weight-bold text-dark text-2">{{ trans('Transmission') }}</label>
        <v-select
          v-model="transmissionType"
          :class="{ error: hasError('transmission') }"
          :placeholder="trans('Transmission')"
          :reduce="option => option.value"
          :loading="!transmissions || !transmissions.length"
          :disabled="!transmissions || !transmissions.length"
          :options="transmissions"
        />
        <label class="error" v-if="hasError('transmission')">{{ errors['transmission'][0] }}</label>
      </div>

      <!-- Speedometer Display -->
      <div class="form-group col-lg-4">
        <label class="font-weight-bold text-dark text-2">{{ trans('Speedometer Display') }}</label>
        <v-select
          v-model="isSpeedometerInMiles"
          :class="{ error: hasError('speedometer_mph') }"
          :placeholder="trans('Speedometer Display')"
          :clearable="false"
          :reduce="option => option.value" :options="[
                    { value: true, label: 'Miles (mph)' },
                    { value: false, label: 'Kilometres (kph)' },
                ]"
        />
        <label class="error" v-if="hasError('speedometer_mph')">{{ errors['speedometer_mph'][0] }}</label>
      </div>

      <!-- Odometer -->
      <div class="form-group col-lg-4">
        <label for="odometer" class="font-weight-bold text-dark text-2">{{ trans('Odometer Value') }}</label>
        <input
          id="odometer"
          type="number"
          placeholder="123123"
          v-model="odometerValue"
          class="form-control no-arrows" :class="{ error: hasError('odometer') }"
        />
        <label class="error" v-if="hasError('odometer')">{{ errors['odometer'][0] }}</label>
      </div>
    </div>
  </div>
</template>

<script>
  import PowerInput from './VehicleFilter/PowerInput';
  import CapacityInput from './VehicleFilter/CapacityInput';
  import {apiClient, apiFetchEnum} from '../../api';

  export default {
    name: "VehicleFilter",

    components: {
      CapacityInput,
      PowerInput,
    },

    props: {
      vehicleManufacturer: {
        type: String,
      },
      vehicleModel: {
        type: String,
      },
      vehicleBodyType: {
        type: [String, Number],
      },
      productionYear: {
        type: [String, Number],
      },
      color: {
        type: Object,
      },
      vin: {
        validator: prop => typeof prop === 'string' || prop === null,
        required: true,
      },
      reg_date: {
        type: Object,
        required: true,
      },
      reg_plate: {
        validator: prop => typeof prop === 'string' || prop === null,
        required: true,
      },
      fuel: {
        validator: prop => typeof prop === 'number' || prop === null,
        required: true,
      },
      transmission: {
        validator: prop => typeof prop === 'number' || prop === null,
        required: true,
      },
      speedometer_mph: {
        validator: prop => typeof prop === 'boolean' || prop === null,
        required: true,
      },
      odometer: {
        validator: prop => typeof prop === 'number' || typeof prop === 'string' || prop === null,
        required: true,
      },
      errors: {
        validator: prop => typeof prop === 'object' || prop === null,
        required: true,
        default: {},
      },
      capacity: {
        validator: prop => typeof prop === 'number' || typeof prop === 'string' || prop === null,
        required: true,
      },
      power: {
        validator: prop => typeof prop === 'number' || typeof prop === 'string' || prop === null,
        required: true,
      },
    },

    data() {
      return {
        manufacturers: [],
        vehicles: [],
        allBodyTypes: [],
        fuelTypes: [],
        colors: [],
        transmissions: [],
        allMonths: null,

        manufacturer: null,
        model: null,
        bodyType: null,
        prodYear: this.productionYear,
        regDate: this.reg_date,
        regPlate: this.reg_plate,
        fuelType: this.fuel,
        vinValue: this.vin,

        powerValue: this.power,
        powerUnit: 'hp',
        powerHp: this.power,
        capacityValue: this.capacity,
        capacityUnit: 'cc',
        capacityCc: this.capacity,
        transmissionType: this.transmission,
        isSpeedometerInMiles: this.speedometer_mph,
        odometerValue: this.odometer,
      }
    },

    computed: {
      months() {
        return this.allMonths || [
          {value: 1, label: 'January'},
          {value: 2, label: 'February'},
          {value: 3, label: 'March'},
          {value: 4, label: 'April'},
          {value: 5, label: 'May'},
          {value: 6, label: 'June'},
          {value: 7, label: 'July'},
          {value: 8, label: 'August'},
          {value: 9, label: 'September'},
          {value: 10, label: 'October'},
          {value: 11, label: 'November'},
          {value: 12, label: 'December'},
        ];
      },

      /**
       * Years that could be used to select in First Registration field
       */
      regYears() {
        const result = [];
        const startYear = this.year ? this.year.value : 1900;
        const currentYear = (new Date()).getFullYear();

        for (let i = startYear; i <= currentYear; i++) {
          result.unshift(i);
        }

        return result;
      },

      models() {
        const models = [];
        this.getFilteredVehicles(['model']).map(item => {
          const model = item.model;
          if (models.indexOf(model) === -1) {
            models.push(model);
          }
        });

        const result = models.sort();

        if (result.length === 1) {
          this.model = result[0];
        }
        return result;
      },

      years() {
        const years = [];
        this.getFilteredVehicles(['year']).map(item => {
          const prodFrom = item.prod_from || '';
          const prodTo = item.prod_to || (new Date()).getUTCFullYear();

          for (let i = prodFrom; i <= prodTo; i++) {
            if (years.indexOf(i) === -1) {
              years.push(i);
            }
          }
        });

        if (years.length === 1 && years[0].prod_from) {
          this.year = years[0].prod_from;
        }
        return years;
      },

      bodyTypes() {
        const bodyTypes = [];
        this.getFilteredVehicles(['body_type']).map(item => {
          const bodyType = item.body_type_id;
          if (bodyTypes.indexOf(bodyType) === -1) {
            bodyTypes.push(bodyType);
          }
        });

        const result = this.allBodyTypes.filter(item => bodyTypes.indexOf(item.value) !== -1);

        if (result.length === 1 && result[0].value) {
          this.bodyType = result[0].value;
        }
        return result;
      },
    },

    mounted() {
      Promise
        .all([
          apiFetchEnum('manufacturer'),
          apiFetchEnum('body_type'),
          apiFetchEnum('color'),
          apiFetchEnum('fuel_type'),
          apiFetchEnum('transmission'),
          apiFetchEnum('month'),
        ])
        .then(values => {
          [
            this.manufacturers,
            this.allBodyTypes,
            this.colors,
            this.fuelTypes,
            this.transmissions,
            this.allMonths,
          ] = values;
        });
    },

    methods: {
      /**
       * Fetch all the vehicles of selected manufacturer
       *
       * @param manufacturerId
       */
      fetchVehicles(manufacturerId) {
        return new Promise((resolve, reject) => {
          apiClient
            .get('/api/vehicle/filter', {
              params: {
                manufacturer: manufacturerId,
              },
            })
            .then(response => {
              if (response.data && response.data.vehicles) {
                resolve(response.data.vehicles.data);
              }
              reject('No data');
            })
            .catch(e => {
              reject(e);
            })
        });
      },

      /**
       * Use selected fields to filter the fetched vehicle array
       *
       * @param excludeFields Array of fields that should be excluded from filtering
       * @returns object[]
       */
      getFilteredVehicles(excludeFields) {
        let result = this.vehicles || [];
        excludeFields = excludeFields || [];
        const useModelFilter = excludeFields.indexOf('model') === -1;
        const useBodyFilter = excludeFields.indexOf('body_type') === -1;

        if (useModelFilter && this.model) {
          const selectedModel = this.model;
          result = result.filter(item => (item.model === selectedModel))
        }

        if (useBodyFilter && this.bodyType) {
          const selectedBody = this.bodyType;
          result = result.filter(item => (item.body_type_id === selectedBody))
        }

        if (result.length >= 1) {
          this.capacityValue = result[0].volume;
          this.capacityUnit = 'cc';

          this.powerValue = result[0].power;
          this.powerUnit = 'hp';
        }

        return result;
      },

      powerHpHandler(val) {
        this.$emit('update:power', val);
      },

      capacityCcHandler(val) {
        this.$emit('update:capacity', val);
      },

      hasError(fieldName) {
        return this.errors.hasOwnProperty(fieldName);
      }
    },

    watch: {
      manufacturer(item) {
        this.fetchVehicles(item.value).then(result => {
          this.vehicles = result;
        })
      },

      prodYear(val) {
        this.$emit('update:productionYear', val)
      },

      regPlate(val) {
        this.$emit('update:reg_plate', val);
      },

      fuelType(val) {
        this.$emit('update:fuel', val);
      },

      powerHp(val) {
        this.$emit('update:power', val)
      },

      capacityCc(val) {
        this.$emit('update:capacity', val)
      },

      transmissionType(val) {
        this.$emit('update:transmission', val)
      },

      isSpeedometerInMiles(val) {
        this.$emit('update:speedometer_mph', val)
      },

      odometerValue(val) {
        this.$emit('update:odometer', Number(val))
      },
    },
  }
</script>
